/**
 * Pure state machine for HomeBackground image cross-fade layers.
 * Kept out of the Svelte component so first-paint vs fade-in rules can be unit-tested.
 */

export type BgLayer = {
	id: number;
	src: string;
	on: boolean;
	instant?: boolean;
};

export type BgLayerState = {
	layers: BgLayer[];
	currentSrc: string | null;
	seq: number;
	/** When true the new layer starts hidden and the caller must turn it on after paint. */
	needsFadeIn: boolean;
	incomingId: number | null;
};

export const emptyBgLayerState = (): BgLayerState => ({
	layers: [],
	currentSrc: null,
	seq: 0,
	needsFadeIn: false,
	incomingId: null,
});

/**
 * Apply a new background src.
 * - Empty src fades existing layers out (color-class / no-image path).
 * - If no layer is currently visible, the new layer starts ON (first paint / recover from empty).
 * - Otherwise the new layer starts OFF so CSS can cross-fade after the next frames.
 */
export function applyBgSrc(state: BgLayerState, src: string): BgLayerState {
	if (src === state.currentSrc) {
		return { ...state, needsFadeIn: false, incomingId: null };
	}

	if (!src) {
		return {
			...state,
			currentSrc: src,
			layers: state.layers.map((layer) => ({
				...layer,
				on: false,
				instant: false,
			})),
			needsFadeIn: false,
			incomingId: null,
		};
	}

	const visible = state.layers.filter((layer) => layer.on);
	const isImmediate = visible.length === 0;
	const id = state.seq + 1;
	const incoming: BgLayer = {
		id,
		src,
		on: isImmediate,
		instant: isImmediate,
	};

	return {
		layers: [...visible, incoming],
		currentSrc: src,
		seq: id,
		needsFadeIn: !isImmediate,
		incomingId: isImmediate ? null : id,
	};
}

/** Turn on the incoming layer (and turn off the others) after the browser has painted opacity 0. */
export function activateIncomingLayer(state: BgLayerState, incomingId: number): BgLayerState {
	return {
		...state,
		needsFadeIn: false,
		incomingId: null,
		layers: state.layers.map((layer) => ({
			...layer,
			on: layer.id === incomingId,
			instant: false,
		})),
	};
}

/**
 * Drop hidden layers after the CSS fade. Never drop the layer that is still waiting to fade in —
 * a busy main thread can fire this timeout before the rAF that sets `on: true`.
 */
export function pruneHiddenBgLayers(state: BgLayerState): BgLayerState {
	const pendingId = state.incomingId;
	return {
		...state,
		layers: state.layers.filter((layer) => layer.on || layer.id === pendingId),
	};
}
